import * as THREE from 'three'
import Producer from '../Producer.js'

// 螢幕 ↔ 世界座標的集中轉換層：所有「canvas 物件要對齊螢幕」的數學都走這裡，
// 共用同一個相機狀態，避免各元件各自手算 fov / 投影而漂掉。
//
// 兩個方向：
//   A. 3D 帶動 DOM —— worldToScreen()（投影世界點到螢幕 px，放標籤 / 對齊 DOM）
//   B. DOM 帶動 3D —— screenToWorld()（射線打到某 z 平面取交點，把物件放到螢幕位置）
// 加上 fit-to-viewport 系列（visibleSizeAt / fractionToWorldWidth）把「佔視窗幾 %」換成世界尺寸。
//
// 注意：fit-to-viewport 與 worldPerPixel 假設目標平面正對相機（本專案相機固定看 -Z、平面在某 z）。
// 所有方法都讀相機「當下」的 fov/aspect/position → 呼叫前須確保 camera.updateProjectionMatrix() 已執行。
export default class Viewport {
    constructor() {
        this.producer = new Producer()
        this.camera = this.producer.camera // Camera 包裝；.instance 是 THREE.PerspectiveCamera

        // 重用的暫存物件，避免每次轉換配置記憶體
        this._v = new THREE.Vector3()
        this._ndc = new THREE.Vector2()
        this._ray = new THREE.Raycaster()
        this._plane = new THREE.Plane()
        this._zNormal = new THREE.Vector3(0, 0, 1)

        // --grid-gap / --grid-padding 現在是 clamp() 表達式：custom property 的
        // computed value 不會把 clamp()/calc() 解析成像素（只有套用在真實 CSS 屬性上
        // 才會被解析），所以用一個隱藏探針元素承接，讀它的 used value 才拿得到 px
        this._gridProbe = document.createElement('div')
        this._gridProbe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;left:-9999px;top:-9999px'
        document.body.appendChild(this._gridProbe)
    }

    get instance() {
        return this.camera.instance
    }

    // 以 CSS px 為基準（與 Interaction 的 normalized、worldToScreen 一致）
    get width() {
        return window.innerWidth
    }

    get height() {
        return window.innerHeight
    }

    // 相機到某 z 平面沿視線的距離（相機看 -Z、平面與相機同在 z 軸 → 等於 z 差）
    distanceTo(planeZ = 0) {
        return Math.abs(this.instance.position.z - planeZ)
    }

    // ── fit-to-viewport：把「視窗比例」換成世界尺寸 ───────────────

    // 該深度（z 平面）上整個視窗可見的世界尺寸
    visibleSizeAt(planeZ = 0) {
        const cam = this.instance
        const h = 2 * this.distanceTo(planeZ) * Math.tan(THREE.MathUtils.degToRad(cam.fov) / 2)
        return { w: h * cam.aspect, h }
    }

    // 該深度下 1 px = 幾世界單位（透視下整個正對平面一致）
    worldPerPixel(planeZ = 0) {
        return this.visibleSizeAt(planeZ).h / this.height
    }

    // px 長度 → 世界長度（在某深度）
    pxToWorld(px, planeZ = 0) {
        return px * this.worldPerPixel(planeZ)
    }

    // 視窗寬的 fx 倍（0..1）→ 世界寬（在某深度）
    fractionToWorldWidth(fx, planeZ = 0) {
        return fx * this.visibleSizeAt(planeZ).w
    }

    // 視窗高的 fy 倍（0..1）→ 世界高（在某深度）
    fractionToWorldHeight(fy, planeZ = 0) {
        return fy * this.visibleSizeAt(planeZ).h
    }

    // ── 12-grid：把 CSS grid 欄位換成螢幕 px ─────────────────────

    // 讀 :root 的 grid 變數（與 CSS 同源，改 variables.css 即生效），回傳某欄區段的螢幕 px 矩形。
    // start/end 為 grid line（1-based），語義同 CSS `grid-column: start / end` → 佔據 start..end-1 欄。
    // 例：gridColumnSpan(7, 12) = 第 7~11 欄（含）；gridColumnSpan(7, 11) = 第 7~10 欄。
    gridColumnSpan(startLine, endLine) {
        const cs = getComputedStyle(document.documentElement)
        const cols = parseInt(cs.getPropertyValue('--grid-columns')) || 12

        this._gridProbe.style.gap = 'var(--grid-gap)'
        this._gridProbe.style.padding = 'var(--grid-padding)'
        const probeStyle = getComputedStyle(this._gridProbe)
        const gap = parseFloat(probeStyle.gap) || 0
        const pad = parseFloat(probeStyle.paddingLeft) || 0

        const col = (this.width - pad * 2 - gap * (cols - 1)) / cols // 單欄寬
        const lineX = (n) => {
            return pad + (n - 1) * (col + gap) // 第 n 條 grid line 的 x（n: 1..cols+1）
        }

        const left = lineX(startLine)
        const right = lineX(endLine) - gap // 區段右緣 = 結束線往左扣掉一個 gutter（gutter 不算進卡片）
        return { left, right, width: right - left, center: (left + right) / 2 }
    }

    // ── 方向 A：世界 → 螢幕 px ────────────────────────────────

    // 回傳 { x, y } CSS px。out 可選，傳入則寫回該物件避免配置
    worldToScreen(vec3, out = {}) {
        const p = this._v.copy(vec3).project(this.instance)
        out.x = (p.x * 0.5 + 0.5) * this.width
        out.y = (-p.y * 0.5 + 0.5) * this.height
        return out
    }

    // ── 方向 B：螢幕 px → 世界 ────────────────────────────────

    // 螢幕 CSS px → NDC（-1..1，y 上正）。out 可選（預設寫回內部重用的 _ndc；
    // Interaction 傳入自己的 normalized 供 raycaster 用 → 換算公式只此一份）
    screenToNDC(x, y, out = this._ndc) {
        out.x = (x / this.width) * 2 - 1
        out.y = -(y / this.height) * 2 + 1
        return out
    }

    // 射線打到 z=planeZ 的平面取交點。target 可選（重用 Vector3）。打不到回 null
    screenToWorld(x, y, planeZ = 0, target = new THREE.Vector3()) {
        this._ray.setFromCamera(this.screenToNDC(x, y), this.instance)
        this._plane.set(this._zNormal, -planeZ) // 平面方程 z·1 - planeZ = 0 → z = planeZ
        return this._ray.ray.intersectPlane(this._plane, target)
    }
}
